import { useState, type ReactNode } from 'react';
import { Layout, Menu, Dropdown, Button, Space, theme as antdTheme } from 'antd';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  AppstoreOutlined,
  GlobalOutlined,
  EditOutlined,
  ReadOutlined,
  TeamOutlined,
  UserOutlined,
  LogoutOutlined,
  BankOutlined,
  BulbOutlined,
  BulbFilled,
  RobotOutlined,
  LayoutOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/lib/auth';
import { useThemeMode } from '@/lib/useThemeMode';
import { BackgroundJobsBadge } from './BackgroundJobsBadge';

const { Header, Sider, Content } = Layout;

const NAV_ITEMS = [
  { key: '/home', icon: <LayoutOutlined />, label: 'Home' },
  { key: '/content', icon: <ProfileOutlined />, label: 'Content Center' },
  { key: '/topics', icon: <AppstoreOutlined />, label: 'Topics' },
  { key: '/languages', icon: <GlobalOutlined />, label: 'Languages' },
  { key: '/translations', icon: <EditOutlined />, label: 'Translations' },
  { key: '/glossary', icon: <ReadOutlined />, label: 'Glossary' },
  { key: '/investors', icon: <BankOutlined />, label: 'Investors' },
  { key: '/users', icon: <TeamOutlined />, label: 'Users' },
  { key: '/ai-settings', icon: <RobotOutlined />, label: 'AI Settings' },
];

// Nav items only super-admins may see.
const SUPER_ONLY = ['/users', '/ai-settings'];

export function AppLayout(_: { children?: ReactNode } = {}) {
  const [collapsed, setCollapsed] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const { email, isSuperAdmin, logout } = useAuth();
  const { token } = antdTheme.useToken();
  const { mode, toggle } = useThemeMode();

  // Highlight whichever nav item starts the current path
  const selectedKey = NAV_ITEMS.find((it) => loc.pathname.startsWith(it.key))?.key ?? '';

  // Hide super-admin-only items for plain admins
  const visibleItems = isSuperAdmin ? NAV_ITEMS : NAV_ITEMS.filter((it) => !SUPER_ONLY.includes(it.key));

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={220}>
        <div style={{ color: '#fff', textAlign: 'center', padding: '16px 0', fontWeight: 600 }}>
          {collapsed ? 'G' : 'GMCC Admin'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={visibleItems}
          onClick={(e) => nav(e.key)}
        />
      </Sider>
      <Layout>
        <Header
          style={{ background: token.colorBgContainer, paddingRight: 24, textAlign: 'right' }}
        >
          <Space>
            <BackgroundJobsBadge />
            <Button
              type="text"
              icon={mode === 'dark' ? <BulbFilled /> : <BulbOutlined />}
              onClick={toggle}
              aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            />
            <Dropdown
              menu={{
                items: [
                  { key: 'logout', icon: <LogoutOutlined />, label: 'Sign out', onClick: logout },
                ],
              }}
            >
              <Button icon={<UserOutlined />}>{email ?? 'Account'}</Button>
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ margin: 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
